import { useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Lock, Save } from "lucide-react";
import { toast } from "sonner";
import { FIELDS } from "@/components/booking-form/registry/fields";
import { TABS } from "@/components/booking-form/registry/tabs";

/**
 * /admin/booking-edit — Phase 9.6
 *
 * Client_admin / site_admin selects which field_keys bookers are
 * allowed to edit on APPROVED bookings. Stored as a text[] on
 * lbs_client_settings.booker_editable_fields.
 *
 * Empty list = locked (default). Any listed key = editable post-approval.
 */

// Curated shortlist of "low-risk" fields we surface at the top for quick-pick.
const QUICK_PICKS = [
  "assigned_driver_id",
  "driver_tba",
  "driver_phone",
  "notes",
  "internal_notes",
  "po_number",
  "external_ref",
];

export default function AdminBookingEdit() {
  const { assignments, hasRole } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient();

  const clientId = isSuperAdmin
    ? (viewAsClientId || null)
    : (assignments?.[0]?.client_id || null);

  const [selected, setSelected] = useState([]);   // string[]
  const [saved, setSaved]       = useState([]);   // last-saved snapshot
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState("");

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("lbs_client_settings")
      .select("booker_editable_fields")
      .eq("client_id", clientId)
      .maybeSingle();
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    const list = data?.booker_editable_fields || [];
    setSelected(list);
    setSaved(list);
    setLoading(false);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (selected.length !== saved.length) return true;
    const a = new Set(selected);
    return saved.some((k) => !a.has(k));
  }, [selected, saved]);

  const toggle = (key) => {
    setSelected((s) => s.includes(key) ? s.filter((x) => x !== key) : [...s, key]);
  };

  const save = async () => {
    // Upsert on client_id — provision the row if missing.
    const { error } = await supabase
      .from("lbs_client_settings")
      .upsert({ client_id: clientId, booker_editable_fields: selected }, { onConflict: "client_id" });
    if (error) { toast.error(error.message); return; }
    setSaved(selected);
    toast.success(`Booker edit rights updated (${selected.length} field${selected.length === 1 ? "" : "s"})`);
  };

  const clearAll = () => setSelected([]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return FIELDS;
    return FIELDS.filter((f) =>
      f.key.toLowerCase().includes(q) || f.label.toLowerCase().includes(q)
    );
  }, [search]);

  if (!hasRole("super_admin") && !hasRole("client_admin") && !hasRole("site_admin")) {
    return <p className="text-sm text-rose-700 p-4">Admins only.</p>;
  }

  return (
    <div className="space-y-4" data-testid="admin-booking-edit-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Lock className="h-6 w-6" /> Booker post-approval edit rights
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Approved bookings are read-only for bookers by default. Whitelist
            specific fields below and bookers will be able to edit those — and
            only those — after approval without going through the &quot;Request
            update&quot; flow.
          </p>
        </div>
        <Button onClick={save}
                disabled={!dirty || !clientId}
                data-testid="booking-edit-save-btn">
          <Save className="h-4 w-4 mr-2" />
          {dirty ? `Save (${selected.length})` : "Saved"}
        </Button>
      </div>

      {!clientId && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          {isSuperAdmin
            ? "Pick a client from the top-bar 'View as client' picker."
            : "No tenant assignment on your account."}
        </div>
      )}

      {clientId && (
        <>
          {/* Quick picks */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Common low-risk fields</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2" data-testid="booking-edit-quick-picks">
                {QUICK_PICKS.map((k) => {
                  const f = FIELDS.find((x) => x.key === k);
                  if (!f) return null;
                  const active = selected.includes(k);
                  return (
                    <button key={k}
                            type="button"
                            onClick={() => toggle(k)}
                            className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                              active
                                ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                                : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                            }`}
                            data-testid={`booking-edit-quick-${k}`}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
              {selected.length > 0 && (
                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                  <span>{selected.length} field{selected.length === 1 ? "" : "s"} whitelisted</span>
                  <button onClick={clearAll}
                          className="text-slate-500 hover:text-rose-600 underline"
                          data-testid="booking-edit-clear-btn">
                    Clear all
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Full field library */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">All registry fields</CardTitle>
              <Input placeholder="Search fields by key or label…"
                     value={search}
                     onChange={(e) => setSearch(e.target.value)}
                     className="mt-2 h-9"
                     data-testid="booking-edit-search" />
            </CardHeader>
            <CardContent>
              {loading && <p className="text-xs text-slate-400">Loading…</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {filtered.map((f) => {
                  const active = selected.includes(f.key);
                  const tabLabel = TABS.find((t) => t.key === f.tab)?.label || f.tab;
                  return (
                    <label key={f.key}
                           className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${
                             active
                               ? "bg-emerald-50 border-emerald-300"
                               : "bg-white border-slate-200 hover:bg-slate-50"
                           }`}
                           data-testid={`booking-edit-row-${f.key}`}>
                      <input type="checkbox"
                             checked={active}
                             onChange={() => toggle(f.key)}
                             className="mt-0.5"
                             data-testid={`booking-edit-check-${f.key}`} />
                      <div className="min-w-0">
                        <div className="text-sm truncate">{f.label}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1">
                          <code className="font-mono">{f.key}</code>
                          <Badge variant="outline" className="text-[10px]">{tabLabel}</Badge>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
